import dayjs from 'dayjs';
import { Fragment } from 'react';
 
import {
  GroupedSalesOrders,
  getDateHeader,
  getTotalCount,
  tableMainHeaders,
} from './helpers/helpers';
import styles from './predicted-actual-table.module.scss';
import { SalesOrdersPredictionResultType } from '@/graphql/contracts/_generated_/contracts';
import PredictedActualTableSkeleton, { ITableColumn } from './components/PredictedActualTableSkeleton';

interface IPredictedActualTable {
  salesOrdersPrediction: SalesOrdersPredictionResultType[];
  isLoadingPrediction: boolean;
  isBillingCyclesPath: boolean;
}

function PredictedActualTable({
  isLoadingPrediction,
  salesOrdersPrediction,
  isBillingCyclesPath,
}: IPredictedActualTable) {
  const tableSubHeaders = Array(salesOrdersPrediction.length).fill(['Predicted', 'Actual']).flat();

  if (isLoadingPrediction) {
    return (
      <div className={styles['sales-orders-skeleton']}>
        <PredictedActualTableSkeleton
          skeletonRowCount={4}
          tableColumns={Object.values(tableMainHeaders) as ITableColumn[]}
        />
      </div>
    );
  }

  if (!salesOrdersPrediction.length) {
    return (
      <div>
        <div className={styles['date-warning-message']}>
          There are no predictions for the past 3 days
        </div>
      </div>
    );
  }

  return (
    <table className={styles['sales-orders-table']}>
      <thead>
        <tr className={styles.mainHeaders}>
          <th rowSpan={3}></th>
          {salesOrdersPrediction.map((prediction: SalesOrdersPredictionResultType) => {
            return (
              <th key={prediction.date} colSpan={2} className={styles.mainHeaders}>
                {dayjs(prediction.date).format('D MMMM')}
              </th>
            );
          })}
        </tr>
        <tr>
          {tableSubHeaders.map((subheader: string, i) => {
            return (
              <th key={subheader + i} className={styles[`${subheader.toLowerCase()}`]}>
                {subheader}
              </th>
            );
          })}
        </tr>
        {!isBillingCyclesPath && (
          <tr>
            {salesOrdersPrediction?.map(
              (prediction: SalesOrdersPredictionResultType, index: number) => {
                return (
                  <Fragment key={String(prediction.date)}>
                    <td className={styles['date-header']}>
                      {prediction.expected ? getDateHeader(prediction.expected, index) : '-'}
                    </td>
                    <td className={styles['date-header']}>
                      {prediction.actual ? getDateHeader(prediction.actual, index) : '-'}
                    </td>
                  </Fragment>
                );
              },
            )}
          </tr>
        )}
      </thead>
      <tbody>
        <tr>
          <th className={styles.subHeaders}>Consolidated</th>
          {salesOrdersPrediction?.map((prediction: SalesOrdersPredictionResultType) => {
            return (
              <Fragment key={String(prediction.date)}>
                <td>{prediction.expected?.consolidated?.slice(-1)[0]?.count}</td>
                <td>{prediction.actual?.consolidated?.slice(-1)[0]?.count}</td>
              </Fragment>
            );
          })}
        </tr>
        <tr>
          <th className={styles.subHeaders}>Non-consolidated</th>
          {salesOrdersPrediction?.map((prediction: SalesOrdersPredictionResultType) => {
            return (
              <Fragment key={String(prediction.date)}>
                <td>{prediction.expected?.nonConsolidated?.slice(-1)[0]?.count} </td>
                <td>{prediction.actual?.nonConsolidated?.slice(-1)[0]?.count}</td>
              </Fragment>
            );
          })}
        </tr>
        <tr>
          <th className={styles.subHeaders}>Total Sales Orders</th>
          {salesOrdersPrediction?.map((prediction: SalesOrdersPredictionResultType) => {
            return (
              <Fragment key={String(prediction.date)}>
                <td>{getTotalCount(prediction.expected as GroupedSalesOrders)}</td>
                <td>{getTotalCount(prediction.actual as GroupedSalesOrders)}</td>
              </Fragment>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

export default PredictedActualTable;
